import React, { useEffect, useState } from 'react';
import { Container, Card } from 'react-bootstrap';
import { companyAPI } from '../../services/api';
import CompanyLayout from '../common/CompanyLayout';

const WebstoreMessages: React.FC = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    companyAPI.getContactMessages().then(res => {
      setMessages(Array.isArray(res.data) ? res.data : []);
    }).finally(() => setLoading(false));
  }, []);

  const unread = messages.filter(m => !m.is_read).length;

  return (
    <CompanyLayout>
    <Container fluid className="py-4 px-4" style={{ maxWidth: 780 }}>
      <div className="mb-4">
        <div style={{ fontWeight: 800, fontSize: '1.4rem', color: '#1e293b' }}>
          Messages
          {unread > 0 && (
            <span style={{ marginLeft: 10, background: '#ef4444', color: 'white', borderRadius: '14px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', verticalAlign: 'middle' }}>
              {unread} new
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: 3 }}>Messages submitted through your store's contact form</div>
      </div>

      <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
        <Card.Body className="p-0">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: '0.9rem' }}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>✉️</div>
              <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No messages yet.</div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={msg.id}>
                {/* Row */}
                <div
                  onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px',
                    borderBottom: i < messages.length - 1 ? '1px solid #f1f5f9' : 'none',
                    cursor: 'pointer',
                    background: expanded === msg.id ? '#f8fafc' : 'white',
                    borderRadius: i === 0 ? '16px 16px 0 0' : i === messages.length - 1 && expanded !== msg.id ? '0 0 16px 16px' : undefined,
                  }}
                >
                  {/* Unread dot */}
                  <div style={{
                    width: 9, height: 9, borderRadius: '50%', flexShrink: 0, marginTop: 6,
                    background: msg.is_read ? 'transparent' : '#3b82f6',
                    border: msg.is_read ? '1.5px solid #cbd5e1' : 'none',
                  }} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontWeight: msg.is_read ? 500 : 700, fontSize: '0.92rem', color: '#1e293b' }}>{msg.name}</span>
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0 }}>{new Date(msg.created_at).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{msg.email}{msg.subject ? ` · ${msg.subject}` : ''}</div>
                    {expanded !== msg.id && (
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 3 }}>{msg.message}</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="d-flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      title={msg.is_read ? 'Mark unread' : 'Mark read'}
                      onClick={async () => {
                        await companyAPI.markContactRead(msg.id, !msg.is_read);
                        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: !m.is_read } : m));
                      }}
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '4px 9px', cursor: 'pointer', fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap' }}
                    >
                      {msg.is_read ? '● Unread' : '✓ Read'}
                    </button>
                    <button
                      title="Delete"
                      onClick={async () => {
                        if (!window.confirm('Delete this message?')) return;
                        await companyAPI.deleteContactMessage(msg.id);
                        setMessages(prev => prev.filter(m => m.id !== msg.id));
                        if (expanded === msg.id) setExpanded(null);
                      }}
                      style={{ background: 'none', border: '1px solid #fee2e2', borderRadius: '7px', padding: '4px 9px', cursor: 'pointer', color: '#ef4444', fontSize: '0.72rem' }}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Expanded body */}
                {expanded === msg.id && (
                  <div style={{
                    background: '#f8fafc', padding: '16px 20px 20px 44px',
                    borderBottom: i < messages.length - 1 ? '1px solid #f1f5f9' : 'none',
                    borderRadius: i === messages.length - 1 ? '0 0 16px 16px' : undefined,
                  }}>
                    {msg.phone && (
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8 }}>📞 {msg.phone}</div>
                    )}
                    <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                    <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
                      <a href={`mailto:${msg.email}${msg.subject ? `?subject=Re: ${encodeURIComponent(msg.subject)}` : ''}`}
                        style={{ fontSize: '0.82rem', color: '#059669', fontWeight: 700, textDecoration: 'none' }}>
                        ✉ Reply via email
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </Card.Body>
      </Card>
    </Container>
    </CompanyLayout>
  );
};

export default WebstoreMessages;
